import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from "react";
import { TraceProvider, useTraceContext, TraceOverlay } from "./features/trace";
import { SettingsPanel } from "./features/settings";
import { ArrowLeft, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
export default function App() {
    return (_jsx(TraceProvider, { children: _jsx(AppShell, {}) }));
}
function AppShell() {
    const { traceOpen, handleOpenTrace, handleOpenDependents, handleGoHome, } = useTraceContext();
    const [mainView, setMainView] = useState("precedents");
    const [settingsOpen, setSettingsOpen] = useState(false);
    const openPrecedents = useCallback(async () => {
        setSettingsOpen(false);
        setMainView("precedents");
        await handleOpenTrace();
    }, [handleOpenTrace]);
    const openDependents = useCallback(async () => {
        setSettingsOpen(false);
        setMainView("dependents");
        await handleOpenDependents();
    }, [handleOpenDependents]);
    useEffect(() => {
        void openPrecedents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        async function ensureTaskpaneVisible() {
            try {
                await Office.addin.showAsTaskpane();
            }
            catch {
                // Already visible or API unavailable
            }
        }
        window.__gridrive = {
            handleTrace: async () => {
                await ensureTaskpaneVisible();
                await openPrecedents();
            },
            handleDependents: async () => {
                await ensureTaskpaneVisible();
                await openDependents();
            },
            handleOpenGridrive: async () => {
                await ensureTaskpaneVisible();
                await openPrecedents();
            },
        };
        return () => { delete window.__gridrive; };
    }, [openDependents, openPrecedents]);
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key !== "Escape")
                return;
            if (traceOpen)
                return;
            e.preventDefault();
            try {
                Office.addin.hide();
            }
            catch { /* API unavailable */ }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [traceOpen]);
    useEffect(() => {
        let disposed = false;
        let removeVisibilityHandler = null;
        if (typeof Office === "undefined" ||
            !Office.addin?.onVisibilityModeChanged) {
            return;
        }
        void Office.addin
            .onVisibilityModeChanged((message) => {
            if (message.visibilityMode === Office.VisibilityMode.hidden) {
                setSettingsOpen(false);
                setMainView("precedents");
                if (traceOpen)
                    handleGoHome();
                return;
            }
            if (message.visibilityMode === Office.VisibilityMode.taskpane) {
                void openPrecedents();
            }
        })
            .then((removeHandler) => {
            if (disposed) {
                void removeHandler();
                return;
            }
            removeVisibilityHandler = removeHandler;
        })
            .catch(() => {
            // Shared runtime visibility events are unavailable outside Office hosts.
        });
        return () => {
            disposed = true;
            if (removeVisibilityHandler)
                void removeVisibilityHandler();
        };
    }, [handleGoHome, openPrecedents, traceOpen]);
    return (_jsxs("div", { className: "flex h-full flex-col bg-background", children: [_jsx("div", { className: "px-3 pt-3 pb-1.5", children: _jsxs("div", { className: "flex items-center gap-5", children: [settingsOpen ? (_jsx(UtilityTitle, { title: "Settings", onBack: () => void openPrecedents() })) : (_jsxs(_Fragment, { children: [_jsx(PageTab, { label: "Precedent", active: mainView === "precedents", onClick: () => void openPrecedents() }), _jsx(PageTab, { label: "Dependent", active: mainView === "dependents", onClick: () => void openDependents() })] })), _jsx(SettingsButton, { active: settingsOpen, onClick: () => {
                                if (traceOpen)
                                    handleGoHome();
                                setSettingsOpen(true);
                            } })] }) }), _jsxs("div", { className: "relative flex-1 overflow-hidden", children: [settingsOpen && !traceOpen && _jsx(SettingsPanel, {}), !settingsOpen && traceOpen && _jsx(TraceOverlay, {})] })] }));
}
function PageTab({ label, active, onClick, }) {
    return (_jsx("button", { type: "button", onClick: onClick, "aria-current": active ? "page" : undefined, className: cn("relative rounded-sm pb-1 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20", active
            ? "text-foreground after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-foreground"
            : "text-muted-foreground hover:text-foreground"), children: label }));
}
function UtilityTitle({ title, onBack }) {
    return (_jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [_jsx("button", { type: "button", className: "flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20", onClick: onBack, title: "Back", "aria-label": "Back", children: _jsx(ArrowLeft, { className: "size-4" }) }), _jsx("span", { className: "truncate text-sm font-semibold text-foreground", children: title })] }));
}
function SettingsButton({ active, onClick }) {
    return (_jsx("button", { type: "button", className: cn("ml-auto mr-6 flex size-8 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20", active
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"), onClick: onClick, title: "Settings", "aria-label": "Settings", "aria-pressed": active, children: _jsx(Settings, { className: "size-4" }) }));
}
